import 'svelte/internal/flags/legacy';
import * as $ from 'svelte/internal/client';
import { tick, getContext, setContext, beforeUpdate, afterUpdate } from 'svelte';
import { derived, get, writable, readable } from 'svelte/store';

function keyEnter(fn) {
    return function (e) {
        return e.key === 'Enter' || e.key === ' ' && !e.preventDefault()  // prevent page scroll down
            ? fn.call(this, e)
            : undefined;
    };
}

function setContent(node, content) {
    let actions = {
        update(content) {
            if (typeof content == 'string') {
                node.innerText = content;
            } else if (content?.domNodes) {
                node.replaceChildren(...content.domNodes);
            } else if (content?.html) {
                node.innerHTML = content.html;
            }
        }
    };
    actions.update(content);

    return actions;
}

/** Dispatch event occurred outside of node */
function outsideEvent(node, type) {

    const handlePointerDown = jsEvent => {
        if (node && !node.contains(jsEvent.target)) {
            node.dispatchEvent(
                new CustomEvent(type + 'outside', {detail: {jsEvent}})
            );
        }
    };

    document.addEventListener(type, handlePointerDown, true);

    return {
        destroy() {
            document.removeEventListener(type, handlePointerDown, true);
        }
    };
}

const DAY_IN_SECONDS = 86400;

function createDate(input = undefined) {
    if (input !== undefined) {
        return input instanceof Date ? _fromLocalDate(input) : _fromISOString(input);
    }

    return _fromLocalDate(new Date());
}

function createDuration(input) {
    if (typeof input === 'number') {
        input = {seconds: input};
    } else if (typeof input === 'string') {
        // Expected format hh[:mm[:ss]]
        let seconds = 0, exp = 2;
        for (let part of input.split(':', 3)) {
            seconds += parseInt(part, 10) * Math.pow(60, exp--);
        }
        input = {seconds};
    } else if (input instanceof Date) {
        input = {hours: input.getUTCHours(), minutes: input.getUTCMinutes(), seconds: input.getUTCSeconds()};
    }

    let weeks = input.weeks || input.week || 0;

    return {
        years: input.years || input.year || 0,
        months: input.months || input.month || 0,
        days: weeks * 7 + (input.days || input.day || 0),
        seconds: (input.hours || input.hour || 0) * 60 * 60 +
            (input.minutes || input.minute || 0) * 60 +
            (input.seconds || input.second || 0),
        inWeeks: !!weeks
    };
}

function cloneDate(date) {
    return new Date(date.getTime());
}

function addDuration(date, duration, x = 1) {
    date.setUTCFullYear(date.getUTCFullYear() + x * duration.years);
    let month = date.getUTCMonth() + x * duration.months;
    date.setUTCMonth(month);
    month %= 12;
    if (month < 0) {
        month += 12;
    }
    while (date.getUTCMonth() !== month) {
        subtractDay(date);
    }
    date.setUTCDate(date.getUTCDate() + x * duration.days);
    date.setUTCSeconds(date.getUTCSeconds() + x * duration.seconds);

    return date;
}

function subtractDuration(date, duration, x = 1) {
    return addDuration(date, duration, -x);
}

function addDay(date, x = 1) {
    date.setUTCDate(date.getUTCDate() + x);

    return date;
}

function subtractDay(date, x = 1) {
    return addDay(date, -x);
}

function setMidnight(date) {
    date.setUTCHours(0, 0, 0, 0);

    return date;
}

function toLocalDate(date) {
    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
    );
}

function toISOString(date, len = 19) {
    return date.toISOString().substring(0, len);
}

function datesEqual(date1, ...dates2) {
    return dates2.every(date2 => date1.getTime() === date2.getTime());
}

function nextClosestDay(date, day) {
    let diff = day - date.getUTCDay();
    date.setUTCDate(date.getUTCDate() + (diff >= 0 ? diff : diff + 7));
    return date;
}

function prevClosestDay(date, day) {
    let diff = day - date.getUTCDay();
    date.setUTCDate(date.getUTCDate() + (diff <= 0 ? diff : diff - 7));
    return date;
}

/**
 * Check whether given date is string which contains no time part
  */
function noTimePart(date) {
    return typeof date === 'string' && date.length <= 10;
}

/**
 * Copy time from one date to another
 */
function copyTime(toDate, fromDate) {
    toDate.setUTCHours(fromDate.getUTCHours(), fromDate.getUTCMinutes(), fromDate.getUTCSeconds(), 0);

    return toDate;
}

/**
 * Get duration value in seconds
 */
function toSeconds(duration) {
    return duration.seconds;
}

/**
 * Move the date forward (when pressing the next button)
 */
function nextDate(date, duration) {
    addDuration(date, duration);
    return date;
}

/**
 * Move the date backward (when pressing the prev button)
 */
function prevDate(date, duration, hiddenDays) {
    subtractDuration(date, duration);
    if (hiddenDays.length && hiddenDays.length < 7) {
        while (hiddenDays.includes(date.getUTCDay())) {
            subtractDay(date);
        }
    }
    return date;
}

/**
 * For a given date, get its week number
 *  - ISO @see https://stackoverflow.com/questions/6117814/get-week-of-year-in-javascript-like-in-php
 *  - Western @see https://en.wikipedia.org/wiki/Week#Other_week_numbering_systems
 */
function getWeekNumber(date, firstDay) {
    // Copy date so don't modify original
    date = cloneDate(date);
    if (firstDay == 0) {  // Western
        // Set to nearest Saturday: current date + 6 - current day number
        date.setUTCDate(date.getUTCDate() + 6 - date.getUTCDay());
    } else {  // ISO
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    }
    // Get first day of year
    let yearStart = new Date(Date.UTC(date.getUTCFullYear(),0,1));
    // Calculate full weeks to `date`
    return Math.ceil((((date - yearStart) / 1000 / DAY_IN_SECONDS) + 1) / 7);
}

/**
 * Private functions
 */

function _fromLocalDate(date) {
    return new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
    ));
}

function _fromISOString(str) {
    const parts = str.match(/\d+/g);
    return new Date(Date.UTC(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
        Number(parts[3] || 0),
        Number(parts[4] || 0),
        Number(parts[5] || 0)
    ));
}

function assign(...args) {
    return Object.assign(...args);
}

function keys(object) {
    return Object.keys(object);
}

function floor(value) {
    return Math.floor(value);
}

function ceil(value) {
    return Math.ceil(value);
}

function min(...args) {
    return Math.min(...args);
}

function max(...args) {
    return Math.max(...args);
}

function symbol() {
    return Symbol('ec');
}

function isArray(value) {
    return Array.isArray(value);
}

function isFunction(value) {
    return typeof value === 'function';
}

function run(fn) {
    return fn();
}

function runAll(fns) {
    fns.forEach(run);
}

function noop() {}

const identity = (x) => x;

function debounce(fn, handle, queueStore) {
    queueStore.update(queue => queue.set(handle, fn));
}

function flushDebounce(queue) {
    runAll(queue);
    queue.clear();
}

function task(fn, handle, tasks) {
    handle ??= fn;
    if (!tasks.has(handle)) {
        tasks.set(handle, setTimeout(() => {
            tasks.delete(handle);
            fn();
        }));
    }
}

let payloadProp = symbol();
function setPayload(obj, payload) {
    obj[payloadProp] = payload;
}

function hasPayload(obj) {
    return !!obj?.[payloadProp];
}

function getPayload(obj) {
    return obj[payloadProp];
}

function createElement(tag, className, content, attrs = []) {
    let el = document.createElement(tag);
    el.className = className;
    if (typeof content == 'string') {
        el.innerText = content;
    } else if (content.domNodes) {
        el.replaceChildren(...content.domNodes);
    } else if (content.html) {
        el.innerHTML = content.html;
    }
    for (let attr of attrs) {
        el.setAttribute(...attr);
    }
    return el;
}

function hasYScroll(el) {
    return el.scrollHeight > el.clientHeight;
}

function rect(el) {
    return el.getBoundingClientRect();
}

function ancestor(el, up) {
    while (up--) {
        el = el.parentElement;
    }
    return el;
}

function height(el) {
    return rect(el).height;
}

function getElementWithPayload(x, y, root = document) {
    for (let el of root.elementsFromPoint(x, y)) {
        if (hasPayload(el)) {
            return el;
        }
        /** @see https://github.com/vkurko/calendar/issues/142 */
        if (el.shadowRoot) {
            let shadowEl = getElementWithPayload(x, y, el.shadowRoot);
            if (shadowEl) {
                return shadowEl;
            }
        }
    }
    return null;
}

function listen(node, event, handler, options) {
    node.addEventListener(event, handler, options);
    return () => node.removeEventListener(event, handler, options);
}

function createView(view, _viewTitle, _currentRange, _activeRange) {
    return {
        type: view,
        title: _viewTitle,
        currentStart: _currentRange.start,
        currentEnd: _currentRange.end,
        activeStart: _activeRange.start,
        activeEnd: _activeRange.end,
        calendar: undefined
    };
}

function toViewWithLocalDates(view) {
    view = assign({}, view);
    view.currentStart = toLocalDate(view.currentStart);
    view.currentEnd = toLocalDate(view.currentEnd);
    view.activeStart = toLocalDate(view.activeStart);
    view.activeEnd = toLocalDate(view.activeEnd);

    return view;
}

function listView(view) {
    return view.startsWith('list');
}

function timelineView(view) {
    return view.includes('Timeline');
}

let eventId = 1;
function createEvents(input) {
    return input.map(event => {
        let result = {
            id: 'id' in event ? String(event.id) : `{generated-${eventId++}}`,
            resourceIds: toArrayProp(event, 'resourceId').map(String),
            allDay: event.allDay ?? (noTimePart(event.start) && noTimePart(event.end)),
            start: createDate(event.start),
            end: createDate(event.end),
            title: event.title ?? '',
            editable: event.editable,
            startEditable: event.startEditable,
            durationEditable: event.durationEditable,
            display: event.display ?? 'auto',
            extendedProps: event.extendedProps ?? {},
            backgroundColor: event.backgroundColor ?? event.color,
            textColor: event.textColor,
            classNames: toArrayProp(event, 'className'),
            styles: toArrayProp(event, 'style')
        };

        if (result.allDay) {
            // Make sure all-day events start and end at midnight
            setMidnight(result.start);
            let end = cloneDate(result.end);
            setMidnight(result.end);
            if (
                !datesEqual(result.end, end) ||
                datesEqual(result.end, result.start)  /** @see https://github.com/vkurko/calendar/issues/50 */
            ) {
                addDay(result.end);
            }
        }

        return result;
    });
}

function toArrayProp(input, propName) {
    let result = input[propName + 's'] ?? input[propName] ?? [];
    return isArray(result) ? result : [result];
}

function createEventSources(input) {
    return input.map(source => ({
        events: source.events,
        url: (source.url && source.url.trimEnd('&')) || '',
        method: (source.method && source.method.toUpperCase()) || 'GET',
        extraParams: source.extraParams || {}
    }));
}

function createEventChunk(event, start, end) {
    let chunk = {
        start: event.start > start ? event.start : start,
        end: event.end < end ? event.end : end,
        event
    };
    chunk.zeroDuration = datesEqual(chunk.start, chunk.end);

    return chunk;
}

function sortEventChunks(chunks) {
    // Sort by start date (all-day events always on top)
    chunks.sort((a, b) => a.start - b.start || b.event.allDay - a.event.allDay);
}

function createEventContent(chunk, displayEventEnd, eventContent, theme, _intlEventTime, _view) {
    let timeText = _intlEventTime.formatRange(
        chunk.start,
        displayEventEnd && chunk.event.display !== 'pointer' && !chunk.zeroDuration
            ? copyTime(cloneDate(chunk.start), chunk.end)  // make Intl.formatRange output only the time part
            : chunk.start
    );
    let content;

    if (eventContent) {
        content = isFunction(eventContent)
            ? eventContent({
                event: toEventWithLocalDates(chunk.event),
                timeText,
                view: toViewWithLocalDates(_view)
            })
            : eventContent;
    }

    if (content === undefined) {
        let domNodes;
        switch (chunk.event.display) {
            case 'background':
                domNodes = [];
                break;
            case 'pointer':
                domNodes = [createTimeElement(timeText, chunk, theme)];
                break;
            default:
                domNodes = [
                    ...chunk.event.allDay ? [] : [createTimeElement(timeText, chunk, theme)],
                    createElement('h4', theme.eventTitle, chunk.event.title)
                ];
        }
        content = {domNodes};
    }

    return [timeText, content];
}

function createTimeElement(timeText, chunk, theme) {
    return createElement(
        'time',
        theme.eventTime,
        timeText,
        [['datetime', toISOString(chunk.start)]]
    );
}

function createEventClasses(eventClassNames, event, _view) {
    let result = event.classNames;
    if (eventClassNames) {
        if (isFunction(eventClassNames)) {
            eventClassNames = eventClassNames({
                event: toEventWithLocalDates(event),
                view: toViewWithLocalDates(_view)
            });
        }
        result = [
            ...isArray(eventClassNames) ? eventClassNames : [eventClassNames],
            ...result
        ];
    }
    return result;
}

function toEventWithLocalDates(event) {
    return _cloneEvent(event, toLocalDate);
}

function cloneEvent(event) {
    return _cloneEvent(event, cloneDate);
}

function _cloneEvent(event, dateFn) {
    event = assign({}, event);
    event.start = dateFn(event.start);
    event.end = dateFn(event.end);

    return event;
}

/**
 * Prepare event chunks for month view and all-day slot in week view
 */
function prepareEventChunks(chunks, hiddenDays) {
    let longChunks = {};

    if (chunks.length) {
        sortEventChunks(chunks);

        let prevChunk;
        for (let chunk of chunks) {
            let dates = [];
            let date = setMidnight(cloneDate(chunk.start));
            while (chunk.end > date) {
                if (!hiddenDays.includes(date.getUTCDay())) {
                    dates.push(cloneDate(date));
                    if (dates.length > 1) {
                        let key = date.getTime();
                        if (longChunks[key]) {
                            longChunks[key].chunks.push(chunk);
                        } else {
                            longChunks[key] = {
                                sorted: false,
                                chunks: [chunk]
                            };
                        }
                    }
                }
                addDay(date);
            }
            if (dates.length) {
                chunk.date = dates[0];
                chunk.days = dates.length;
                chunk.dates = dates;
                // Adjust the start and end dates of the chunk if hidden days affected them
                if (chunk.start < dates[0]) {
                    chunk.start = dates[0];
                }
                let maxEnd = addDay(cloneDate(dates.at(-1)));
                if (chunk.end > maxEnd) {
                    chunk.end = maxEnd;
                }
            } else {
                chunk.date = setMidnight(cloneDate(chunk.start));
                chunk.days = 1;
                chunk.dates = [chunk.date];
            }

            if (prevChunk && datesEqual(prevChunk.date, chunk.date)) {
                chunk.prev = prevChunk;
            }
            prevChunk = chunk;
        }
    }

    return longChunks;
}

function repositionEvent(chunk, longChunks, height) {
    chunk.top = 0;
    if (chunk.prev) {
        chunk.top = chunk.prev.bottom + 1;
    }
    chunk.bottom = chunk.top + height;
    let margin = 1;
    let key = chunk.date.getTime();
    if (longChunks[key]) {
        if (!longChunks[key].sorted) {
            longChunks[key].chunks.sort((a, b) => a.top - b.top);
            longChunks[key].sorted = true;
        }
        for (let longChunk of longChunks[key].chunks) {
            if (chunk.top < longChunk.bottom && chunk.bottom > longChunk.top) {
                let offset = longChunk.bottom - chunk.top + 1;
                margin += offset;
                chunk.top += offset;
                chunk.bottom += offset;
            }
        }
    }

    return margin;
}

function runReposition(refs, data) {
    refs.length = data.length;
    let result = [];
    for (let ref of refs) {
        result.push(ref?.reposition?.());
    }
    return result;
}

/**
 * Check whether the event intersects with the given date range and resources
 * @param event
 * @param start
 * @param end
 * @param resources
 * @return boolean
 */
function eventIntersects(event, start, end, resources) {
    if (event.start < end && event.end > start) {
        if (resources) {
            if (!isArray(resources)) {
                resources = [resources];
            }
            return resources.some(resource => event.resourceIds.includes(resource.id));
        }
        return true;
    }
    return false;
}

function helperEvent(display) {
    return previewEvent(display) || ghostEvent(display) || pointerEvent(display);
}

function bgEvent(display) {
    return display === 'background';
}

function previewEvent(display) {
    return display === 'preview';
}

function ghostEvent(display) {
    return display === 'ghost';
}

function pointerEvent(display) {
    return display === 'pointer';
}

function btnTextDay(text) {
    return btnText(text, 'day');
}

function btnTextWeek(text) {
    return btnText(text, 'week');
}

function btnTextMonth(text) {
    return btnText(text, 'month');
}

function btnTextYear(text) {
    return btnText(text, 'year');
}

function btnText(text, period) {
    return {
        ...text,
        next: 'Next ' + period,
        prev: 'Previous ' + period
    };
}

function themeView(view) {
    return theme => ({...theme, view});
}

function createDateRange(input) {
    let start, end;
    if (input) {
        ({start, end} = input);
        if (start) {
            start = setMidnight(createDate(start));
        }
        if (end) {
            end = setMidnight(createDate(end));
        }
    }
    return {start, end};
}

function outsideRange(date, range) {
    return range.start && date < range.start || range.end && date > range.end;
}

function limitToRange(date, range) {
    if (range.start && date < range.start) {
        date = range.start;
    }
    if (range.end && date > range.end) {
        date = range.end;
    }
    return date;
}

function createResources(input) {
    let result = [];
    _createResources(input, 0, result);
    return result;
}

function _createResources(input, level, flat) {
    let result = [];
    for (let item of input) {
        let resource = createResource(item);
        result.push(resource);
        flat.push(resource);
        let payload = {
            level,
            children: [],
            expanded: true,
            hidden: false
        };
        setPayload(resource, payload);
        if (item.children) {
            payload.children = _createResources(item.children, level + 1, flat);
        }
    }
    return result;
}

function createResource(input) {
    return {
        id: String(input.id),
        title: input.title || '',
        eventBackgroundColor: input.eventBackgroundColor,
        eventTextColor: input.eventTextColor,
        extendedProps: input.extendedProps ?? {}
    };
}

function resourceBackgroundColor(event, resources) {
    return findResource(event, resources)?.eventBackgroundColor;
}

function resourceTextColor(event, resources) {
    return findResource(event, resources)?.eventTextColor;
}

function findResource(event, resources) {
    return resources.find(resource => event.resourceIds.includes(resource.id));
}

function intl(locale, format) {
    return derived([locale, format], ([$locale, $format]) => {
        let intl = isFunction($format)
            ? {format: $format}
            : new Intl.DateTimeFormat($locale, $format);
        return {
            format: date => intl.format(toLocalDate(date))
        };
    });
}

function intlRange(locale, format) {
    return derived([locale, format], ([$locale, $format]) => {
        let formatRange;
        if (isFunction($format)) {
            formatRange = $format;
        } else {
            let intl = new Intl.DateTimeFormat($locale, $format);
            formatRange = (start, end) => {
                if (start <= end) {
                    return intl.formatRange(start, end);
                } else {
                    // In iOS 16 and older, intl.formatRange() throws an exception if the start date is later than the end date.
                    // Therefore, we first swap the parameters, and then swap the resulting parts.
                    /** @see https://github.com/vkurko/calendar/issues/227 */
                    let parts = intl.formatRangeToParts(end, start);
                    let result = '';
                    let sources = ['startRange', 'endRange'];
                    let processed = [false, false];
                    for (let part of parts) {
                        let i = sources.indexOf(part.source);
                        if (i >= 0) {
                            if (!processed[i]) {
                                result += _getParts(sources[1 - i], parts);
                                processed[i] = true;
                            }
                        } else {
                            result += part.value;
                        }
                    }
                    return result;
                }
            };
        }
        return {
            formatRange: (start, end) => formatRange(toLocalDate(start), toLocalDate(end))
        };
    });
}

function _getParts(source, parts) {
    let result = '';
    for (let part of parts) {
        if (part.source == source) {
            result += part.value;
        }
    }
    return result;
}

function viewResources(state) {
    return derived(
        [state.resources, state.filterResourcesWithEvents, state._events, state._activeRange],
        ([$resources, $filterResourcesWithEvents, $_events, $_activeRange]) => {
            let result = $resources.filter(resource => !getPayload(resource).hidden);

            if ($filterResourcesWithEvents) {
                result = $resources.filter(resource => {
                    for (let event of $_events) {
                        if (
                            event.display !== 'background' &&
                            event.resourceIds.includes(resource.id) &&
                            event.start < $_activeRange.end &&
                            event.end > $_activeRange.start
                        ) {
                            return true;
                        }
                    }
                    return false;
                });
            }

            if (!result.length) {
                result = createResources([{}]);
            }

            return result;
        }
    );
}

function createTimes(date, $slotDuration, $_slotTimeLimits, $_intlSlotLabel) {
    date = cloneDate(date);
    let compact = $slotDuration.seconds < 3600;
    let times = [];
    let end = cloneDate(date);
    let i = 1;
    addDuration(date, $_slotTimeLimits.min);
    addDuration(end, $_slotTimeLimits.max);
    while (date < end) {
        times.push([
            toISOString(date),
            $_intlSlotLabel.format(date),
            times.length && (i || !compact)
        ]);
        addDuration(date, $slotDuration);
        i = 1 - i;
    }

    return times;
}

function createSlotTimeLimits($slotMinTime, $slotMaxTime, $flexibleSlotTimeLimits, $_viewDates, $_events) {
    let min$1 = createDuration($slotMinTime);
    let max$1 = createDuration($slotMaxTime);

    if ($flexibleSlotTimeLimits) {
        // If slotMaxTime goes past midnight, then extend it back by a maximum of 24 hours
        let minMin = createDuration(min(toSeconds(min$1), max(0, toSeconds(max$1) - DAY_IN_SECONDS)));
        let maxMax = createDuration(max(toSeconds(max$1), toSeconds(minMin) + DAY_IN_SECONDS));
        let filter = isFunction($flexibleSlotTimeLimits?.eventFilter)
            ? $flexibleSlotTimeLimits.eventFilter
            : event => !bgEvent(event.display);
        loop: for (let date of $_viewDates) {
            let start = addDuration(cloneDate(date), min$1);
            let end = addDuration(cloneDate(date), max$1);
            let minStart = addDuration(cloneDate(date), minMin);
            let maxEnd = addDuration(cloneDate(date), maxMax);
            for (let event of $_events) {
                if (!event.allDay && filter(event) && event.start < maxEnd && event.end > minStart) {
                    if (event.start < start) {
                        let seconds = max((event.start - date) / 1000, toSeconds(minMin));
                        if (seconds < toSeconds(min$1)) {
                            min$1.seconds = seconds;
                        }
                    }
                    if (event.end > end) {
                        let seconds = min((event.end - date) / 1000, toSeconds(maxMax));
                        if (seconds > toSeconds(max$1)) {
                            max$1.seconds = seconds;
                        }
                    }
                    if (toSeconds(min$1) === toSeconds(minMin) && toSeconds(max$1) === toSeconds(maxMax)) {
                        break loop;
                    }
                }
            }
        }
    }

    return {min: min$1, max: max$1};
}

function createOptions(plugins) {
    let options = {
        allDayContent: undefined,
        allDaySlot: true,
        buttonText: {
            today: 'today',
        },
        customButtons: {},
        date: new Date(),
        datesSet: undefined,
        dayHeaderFormat: {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric'
        },
        dayHeaderAriaLabelFormat: {
            dateStyle: 'full'
        },
        displayEventEnd: true,
        duration: { weeks: 1 },
        events: [],
        eventAllUpdated: undefined,
        eventBackgroundColor: undefined,
        eventTextColor: undefined,
        eventClassNames: undefined,
        eventClick: undefined,
        eventColor: undefined,
        eventContent: undefined,
        eventDidMount: undefined,
        eventMouseEnter: undefined,
        eventMouseLeave: undefined,
        eventWrapper: undefined,
        eventSources: [],
        eventTimeFormat: {
            hour: 'numeric',
            minute: '2-digit'
        },
        filterEventsWithResources: false,
        filterResourcesWithEvents: false,
        firstDay: 0,
        flexibleSlotTimeLimits: false,  // ec option
        headerToolbar: {
            start: 'title',
            center: '',
            end: 'today prev,next'
        },
        height: undefined,
        hiddenDays: [],
        highlightedDates: [],  // ec option
        lazyFetching: true,
        loading: undefined,
        locale: undefined,
        nowIndicator: false,
        resourceLabelContent: undefined,
        resourceLabelDidMount: undefined,
        resources: [],
        selectable: false,
        scrollTime: '06:00:00',
        slotDuration: '00:30:00',
        slotEventOverlap: true,
        slotHeight: 24,  // ec option
        slotLabelFormat: {
            hour: 'numeric',
            minute: '2-digit'
        },
        slotMaxTime: '24:00:00',
        slotMinTime: '00:00:00',
        slotWidth: 72,
        theme: {
            allDay: 'ec-all-day',
            active: 'ec-active',
            bgEvent: 'ec-bg-event',
            bgEvents: 'ec-bg-events',
            body: 'ec-body',
            button: 'ec-button',
            buttonGroup: 'ec-button-group',
            calendar: 'ec',
            compact: 'ec-compact',
            content: 'ec-content',
            day: 'ec-day',
            dayHead: 'ec-day-head',
            days: 'ec-days',
            disabled: 'ec-disabled',
            event: 'ec-event',
            eventBody: 'ec-event-body',
            eventTime: 'ec-event-time',
            eventTitle: 'ec-event-title',
            events: 'ec-events',
            extra: 'ec-extra',
            handle: 'ec-handle',
            header: 'ec-header',
            hiddenScroll: 'ec-hidden-scroll',
            highlight: 'ec-highlight',
            icon: 'ec-icon',
            line: 'ec-line',
            lines: 'ec-lines',
            nowIndicator: 'ec-now-indicator',
            otherMonth: 'ec-other-month',
            resource: 'ec-resource',
            sidebar: 'ec-sidebar',
            sidebarTitle: 'ec-sidebar-title',
            today: 'ec-today',
            time: 'ec-time',
            title: 'ec-title',
            toolbar: 'ec-toolbar',
            view: '',
            weekdays: ['ec-sun', 'ec-mon', 'ec-tue', 'ec-wed', 'ec-thu', 'ec-fri', 'ec-sat'],
            withScroll: 'ec-with-scroll'
        },
        titleFormat: {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        },
        validRange: undefined,
        view: undefined,
        viewDidMount: undefined,
        views: {}
    };

    for (let plugin of plugins) {
        plugin.createOptions?.(options);
    }

    return options;
}

function createParsers(plugins) {
    let parsers = {
        date: date => setMidnight(createDate(date)),
        duration: createDuration,
        events: createEvents,
        eventSources: createEventSources,
        hiddenDays: days => [...new Set(days)],
        highlightedDates: dates => dates.map(date => setMidnight(createDate(date))),
        resources: createResources,
        scrollTime: createDuration,
        slotDuration: createDuration,
        slotMaxTime: createDuration,
        slotMinTime: createDuration,
        validRange: createDateRange
    };

    for (let plugin of plugins) {
        plugin.createParsers?.(parsers);
    }

    return parsers;
}

function diff(options, prevOptions) {
    let diff = [];
    for (let key of keys(options)) {
        if (options[key] !== prevOptions[key]) {
            diff.push([key, options[key]]);
        }
    }
    assign(prevOptions, options);

    return diff;
}

function dayGrid(state) {
    return derived(state.view, $view => $view?.startsWith('dayGrid'));
}

function activeRange(state) {
    return derived(
        [state._currentRange, state.firstDay, state.slotMaxTime, state._dayGrid],
        ([$_currentRange, $firstDay, $slotMaxTime, $_dayGrid]) => {
            let start = cloneDate($_currentRange.start);
            let end = cloneDate($_currentRange.end);

            if ($_dayGrid) {
                // First day of week
                prevClosestDay(start, $firstDay);
                nextClosestDay(end, $firstDay);
            } else if ($slotMaxTime.days || $slotMaxTime.seconds > DAY_IN_SECONDS) {
                addDuration(subtractDay(end), $slotMaxTime);
                let start2 = subtractDay(cloneDate(end));
                if (start2 < start) {
                    start = start2;
                }
            }

            return {start, end};
        }
    );
}

function currentRange(state) {
    return derived(
        [state.date, state.duration, state.firstDay],
        ([$date, $duration, $firstDay]) => {
            let start = cloneDate($date), end;
            if ($duration.months) {
                start.setUTCDate(1);
            } else if ($duration.inWeeks) {
                // First day of week
                prevClosestDay(start, $firstDay);
            }
            end = addDuration(cloneDate(start), $duration);

            return {start, end};
        }
    );
}

function viewDates(state) {
    return derived([state._activeRange, state.hiddenDays], ([$_activeRange, $hiddenDays]) => {
        let dates = [];
        let date = setMidnight(cloneDate($_activeRange.start));
        let end = setMidnight(cloneDate($_activeRange.end));
        while (date < end) {
            if (!$hiddenDays.includes(date.getUTCDay())) {
                dates.push(cloneDate(date));
            }
            addDay(date);
        }
        if (!dates.length && $hiddenDays.length && $hiddenDays.length < 7) {
            // Try to move the date
            state.date.update(date => {
                while ($hiddenDays.includes(date.getUTCDay())) {
                    addDay(date);
                }
                return date;
            });
            dates = get(state._viewDates);
        }

        return dates;
    });
}

function viewTitle(state) {
    return derived(
        [state.date, state._activeRange, state._intlTitle, state._dayGrid],
        ([$date, $_activeRange, $_intlTitle, $_dayGrid]) => {
            return $_dayGrid
                ? $_intlTitle.formatRange($date, $date)
                : $_intlTitle.formatRange($_activeRange.start, subtractDay(cloneDate($_activeRange.end)));
        }
    );
}

function view(state) {
    return derived([state.view, state._viewTitle, state._currentRange, state._activeRange], args => createView(...args));
}

function events(state) {
    let _events = writable([]);
    let abortController;
    let fetching = 0;
    let debounceHandle = {};
    derived(
        [state.events, state.eventSources, state._activeRange, state._fetchedRange, state.lazyFetching, state.loading],
        (values, set) => debounce(() => {
            let [$events, $eventSources, $_activeRange, $_fetchedRange, $lazyFetching, $loading] = values;
            if (!$eventSources.length) {
                set($events);
                return;
            }
            // Do not fetch if new range is within the previous one
            if (!$_fetchedRange.start || $_fetchedRange.start > $_activeRange.start || $_fetchedRange.end < $_activeRange.end || !$lazyFetching) {
                if (abortController) {
                    // Abort previous request
                    abortController.abort();
                }
                // Create new abort controller
                abortController = new AbortController();
                // Call loading hook
                if (isFunction($loading) && !fetching) {
                    $loading(true);
                }
                let stopLoading = () => {
                    if (--fetching === 0 && isFunction($loading)) {
                        $loading(false);
                    }
                };
                let events = [];
                // Prepare handlers
                let failure = e => stopLoading();
                let success = data => {
                    events = events.concat(createEvents(data));
                    set(events);
                    stopLoading();
                };
                // Prepare other stuff
                let startStr = toISOString($_activeRange.start);
                let endStr = toISOString($_activeRange.end);
                // Loop over event sources
                for (let source of $eventSources) {
                    if (isFunction(source.events)) {
                        // Events as a function
                        let result = source.events({
                            start: toLocalDate($_activeRange.start),
                            end: toLocalDate($_activeRange.end),
                            startStr,
                            endStr
                        }, success, failure);
                        if (result !== undefined) {
                            Promise.resolve(result).then(success, failure);
                        }
                    } else {
                        // Events as a JSON feed
                        // Prepare params
                        let params = isFunction(source.extraParams) ? source.extraParams() : assign({}, source.extraParams);
                        params.start = startStr;
                        params.end = endStr;
                        params = new URLSearchParams(params);
                        // Prepare fetch
                        let url = source.url, headers = {}, body;
                        if (['GET', 'HEAD'].includes(source.method)) {
                            url += (url.includes('?') ? '&' : '?') + params;
                        } else {
                            headers['content-type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
                            body = String(params);  // Safari 10.1 doesn't convert to string automatically
                        }
                        // Do the fetch
                        fetch(url, {method: source.method, headers, body, signal: abortController.signal, credentials: 'same-origin'})
                            .then(response => response.json())
                            .then(success)
                            .catch(failure);
                    }
                    ++fetching;
                }
                // Save current range for future requests
                $_fetchedRange.start = $_activeRange.start;
                $_fetchedRange.end = $_activeRange.end;
            }
        }, debounceHandle, state._queue),
        []
    ).subscribe(_events.set);

    return _events;
}

function now() {
    return readable(createDate(), set => {
        let interval = setInterval(() => {
            set(createDate());
        }, 1000);

        return () => clearInterval(interval);
    });
}

function today(state) {
    return derived(state._now, $_now => setMidnight(cloneDate($_now)));
}

class State {
    constructor(plugins, input) {
        plugins = plugins || [];

        // Create options
        let options = createOptions(plugins);
        let parsers = createParsers(plugins);

        // Parse options
        options = parseOpts(options, parsers);
        input = parseOpts(input, parsers);

        // Create stores for options
        for (let [option, value] of Object.entries(options)) {
            this[option] = writable(value);
        }

        // Private stores
        this._queue = writable(new Map());  // debounce queue (beforeUpdate)
        this._queue2 = writable(new Map());  // debounce queue (afterUpdate)
        this._tasks = new Map();  // timeout IDs for tasks
        this._auxiliary = writable([]);  // auxiliary components
        this._dayGrid = dayGrid(this);
        this._currentRange = currentRange(this);
        this._activeRange = activeRange(this);
        this._fetchedRange = writable({ start: undefined, end: undefined });
        this._events = events(this);
        this._now = now();
        this._today = today(this);
        this._intlEventTime = intlRange(this.locale, this.eventTimeFormat);
        this._intlSlotLabel = intl(this.locale, this.slotLabelFormat);
        this._intlDayHeader = intl(this.locale, this.dayHeaderFormat);
        this._intlDayHeaderAL = intl(this.locale, this.dayHeaderAriaLabelFormat);
        this._intlTitle = intlRange(this.locale, this.titleFormat);
        this._bodyEl = writable(undefined);
        this._scrollable = writable(false);
        this._viewTitle = viewTitle(this);
        this._viewDates = viewDates(this);
        this._view = view(this);
        this._viewComponent = writable(undefined);
        // Interaction
        this._interaction = writable({});
        this._iEvents = writable([null, null]);  // interaction events: [drag/resize, pointer]
        this._iClasses = writable(identity);  // interaction event css classes
        this._iClass = writable(undefined);  // interaction css class for entire calendar

        // Set & Get
        this._set = (key, value) => {
            if (validKey(key, this)) {
                if (parsers[key]) {
                    value = parsers[key](value);
                }
                this[key].set(value);
            }
        };
        this._get = key => validKey(key, this) ? get(this[key]) : undefined;

        // Let plugins create their private stores
        for (let plugin of plugins) {
            plugin.createStores?.(this);
        }

        if (input.view) {
            // Set initial view based on input
            this.view.set(input.view);
        }

        // Set options for each view
        let views = new Set([...keys(options.views), ...keys(input.views ?? {})]);
        for (let view of views) {
            let defOpts = mergeOpts(options, options.views[view] ?? {});
            let opts = mergeOpts(defOpts, input, input.views?.[view] ?? {});
            let component = opts.component;
            // Make sure we deal with valid opts from now on
            filterOpts(opts, this);
            // Process options
            for (let key of keys(opts)) {
                let { set, _set = set, ...rest } = this[key];

                this[key] = {
                    // Set value in all views
                    set: ['buttonText', 'theme'].includes(key)
                        ? value => {
                            if (isFunction(value)) {
                                let result = value(defOpts[key]);
                                opts[key] = result;
                                set(set === _set ? result : value);
                            } else {
                                opts[key] = value;
                                set(value);
                            }
                        }
                        : value => {
                            opts[key] = value;
                            set(value);
                        },
                    _set,
                    ...rest
                };
            }
            // When view changes...
            this.view.subscribe(newView => {
                if (newView === view) {
                    // switch view component
                    this._viewComponent.set(component);
                    if (isFunction(opts.viewDidMount)) {
                        tick().then(() => opts.viewDidMount(get(this._view)));
                    }
                    // update store values
                    for (let key of keys(opts)) {
                        this[key]._set(opts[key]);
                    }
                }
            });
        }
    }
}

function parseOpts(opts, parsers) {
    let result = { ...opts };
    for (let key of keys(parsers)) {
        if (key in result) {
            result[key] = parsers[key](result[key]);
        }
    }
    if (opts.views) {
        result.views = {};
        for (let view of keys(opts.views)) {
            result.views[view] = parseOpts(opts.views[view], parsers);
        }
    }
    return result;
}

function mergeOpts(...args) {
    let result = {};
    for (let opts of args) {
        let override = {};
        for (let key of ['buttonText', 'theme']) {
            if (isFunction(opts[key])) {
                override[key] = opts[key](result[key]);
            }
        }
        result = {
            ...result,
            ...opts,
            ...override
        };
    }
    return result;
}

function filterOpts(opts, state) {
    keys(opts)
        .filter(key => !validKey(key, state) || key == 'view')
        .forEach(key => delete opts[key]);
}

function validKey(key, state) {
    return state.hasOwnProperty(key) && key[0] !== '_';
}

var root_2 = $.template(`<h2></h2>`);
var root_4 = $.template(`<button><i></i></button>`);
var root_6 = $.template(`<button><i></i></button>`);
var root_8 = $.template(`<button> </button>`);
var root_10 = $.template(`<button></button>`);
var root_12 = $.template(`<button> </button>`);

function Buttons($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $validRange = () => $.store_get(validRange, '$validRange', $$stores);
	const $date = () => $.store_get(date, '$date', $$stores);
	const $duration = () => $.store_get(duration, '$duration', $$stores);
	const $hiddenDays = () => $.store_get(hiddenDays, '$hiddenDays', $$stores);
	const $_currentRange = () => $.store_get(_currentRange, '$_currentRange', $$stores);
	const $_viewDates = () => $.store_get(_viewDates, '$_viewDates', $$stores);
	const $theme = () => $.store_get(theme, '$theme', $$stores);
	const $_viewTitle = () => $.store_get(_viewTitle, '$_viewTitle', $$stores);
	const $buttonText = () => $.store_get(buttonText, '$buttonText', $$stores);
	const $customButtons = () => $.store_get(customButtons, '$customButtons', $$stores);
	const $view = () => $.store_get(view, '$view', $$stores);
	let buttons = $.prop($$props, 'buttons', 8);

	let {
		_currentRange,
		_viewTitle,
		_viewDates,
		buttonText,
		customButtons,
		date,
		duration,
		hiddenDays,
		theme,
		validRange,
		view
	} = getContext('state');

	let today = setMidnight(createDate());

	let prevDisabled = $.mutable_state(),
		nextDisabled = $.mutable_state(),
		todayDisabled = $.mutable_state();

	let running = $.mutable_state(false);

	function isRunning() {
		return $.get(running);
	}

	function test() {
		return $_viewDates().every((date) => outsideRange(date, $validRange()));
	}

	function prev() {
		$.store_set(date, prevDate($date(), $duration(), $hiddenDays()));
	}

	function next() {
		$.store_set(date, nextDate($date(), $duration()));
	}

	$.legacy_pre_effect(
		() => (
			$validRange(),
			$date(),
			$duration(),
			$hiddenDays(),
			$.get(todayDisabled),
			$_currentRange(),
			tick
		),
		() => {
			if (!isRunning()) {
				$.set(running, true);
				$.set(prevDisabled, false);
				$.set(nextDisabled, false);

				if ($validRange().start) {
					let currentDate = cloneDate($date());

					$.store_set(date, prevDate($date(), $duration(), $hiddenDays()));
					$.set(prevDisabled, test());
					$.store_set(date, currentDate);
				}

				if ($validRange().end) {
					let currentDate = cloneDate($date());

					$.store_set(date, nextDate($date(), $duration()));
					$.set(nextDisabled, test());
					$.store_set(date, currentDate);
				}

				$.set(todayDisabled, today >= $_currentRange().start && today < $_currentRange().end);

				if (!$.get(todayDisabled) && ($validRange().start || $validRange().end)) {
					let currentDate = cloneDate($date());

					$.store_set(date, cloneDate(today));
					$.set(todayDisabled, test());
					$.store_set(date, currentDate);
				}

				tick().then(() => $.set(running, false));
			}
		}
	);

	$.legacy_pre_effect_reset();
	$.init();

	var fragment = $.comment();
	var node = $.first_child(fragment);

	$.each(node, 1, buttons, $.index, ($$anchor, button) => {
		var fragment_1 = $.comment();
		var node_1 = $.first_child(fragment_1);

		{
			var consequent = ($$anchor) => {
				var h2 = root_2();

				$.action(h2, ($$node, $$action_arg) => setContent?.($$node, $$action_arg), $_viewTitle);
				$.template_effect(() => $.set_class(h2, $theme().title));
				$.append($$anchor, h2);
			};

			var alternate_4 = ($$anchor) => {
				var fragment_2 = $.comment();
				var node_2 = $.first_child(fragment_2);

				{
					var consequent_1 = ($$anchor) => {
						var button_1 = root_4();
						var i = $.child(button_1);

						$.reset(button_1);

						$.template_effect(() => {
							$.set_class(button_1, `${$theme().button ?? ''} ec-${$.get(button) ?? ''}`);
							$.set_attribute(button_1, 'aria-label', $buttonText().prev);
							$.set_attribute(button_1, 'title', $buttonText().prev);
							button_1.disabled = $.get(prevDisabled);
							$.set_class(i, `${$theme().icon ?? ''} ec-${$.get(button) ?? ''}`);
						});

						$.event('click', button_1, prev);
						$.append($$anchor, button_1);
					};

					var alternate_3 = ($$anchor) => {
						var fragment_3 = $.comment();
						var node_3 = $.first_child(fragment_3);

						{
							var consequent_2 = ($$anchor) => {
								var button_2 = root_6();
								var i_1 = $.child(button_2);

								$.reset(button_2);

								$.template_effect(() => {
									$.set_class(button_2, `${$theme().button ?? ''} ec-${$.get(button) ?? ''}`);
									$.set_attribute(button_2, 'aria-label', $buttonText().next);
									$.set_attribute(button_2, 'title', $buttonText().next);
									button_2.disabled = $.get(nextDisabled);
									$.set_class(i_1, `${$theme().icon ?? ''} ec-${$.get(button) ?? ''}`);
								});

								$.event('click', button_2, next);
								$.append($$anchor, button_2);
							};

							var alternate_2 = ($$anchor) => {
								var fragment_4 = $.comment();
								var node_4 = $.first_child(fragment_4);

								{
									var consequent_3 = ($$anchor) => {
										var button_3 = root_8();
										var text = $.child(button_3, true);

										$.reset(button_3);

										$.template_effect(() => {
											$.set_class(button_3, `${$theme().button ?? ''} ec-${$.get(button) ?? ''}`);
											button_3.disabled = $.get(todayDisabled);
											$.set_text(text, $buttonText()[$.get(button)]);
										});

										$.event('click', button_3, () => $.store_set(date, cloneDate(today)));
										$.append($$anchor, button_3);
									};

									var alternate_1 = ($$anchor) => {
										var fragment_5 = $.comment();
										var node_5 = $.first_child(fragment_5);

										{
											var consequent_4 = ($$anchor) => {
												var button_4 = root_10();

												$.effect(() => $.event('click', button_4, function (...$$args) {
													$customButtons()[$.get(button)].click?.apply(this, $$args);
												}));

												$.action(button_4, ($$node, $$action_arg) => setContent?.($$node, $$action_arg), () => $customButtons()[$.get(button)].text);
												$.template_effect(() => $.set_class(button_4, `${$theme().button ?? ''} ec-${$.get(button) ?? ''}${($customButtons()[$.get(button)].active ? ' ' + $theme().active : '') ?? ''}`));
												$.append($$anchor, button_4);
											};

											var alternate = ($$anchor) => {
												var fragment_6 = $.comment();
												var node_6 = $.first_child(fragment_6);

												{
													var consequent_5 = ($$anchor) => {
														var button_5 = root_12();
														var text_1 = $.child(button_5, true);

														$.reset(button_5);

														$.template_effect(() => {
															$.set_class(button_5, `${$theme().button ?? ''}${($view() === $.get(button) ? ' ' + $theme().active : '') ?? ''} ec-${$.get(button) ?? ''}`);
															$.set_text(text_1, $buttonText()[$.get(button)]);
														});

														$.event('click', button_5, () => $.store_set(view, $.get(button)));
														$.append($$anchor, button_5);
													};

													$.if(
														node_6,
														($$render) => {
															if ($.get(button) != '') $$render(consequent_5);
														},
														true
													);
												}

												$.append($$anchor, fragment_6);
											};

											$.if(
												node_5,
												($$render) => {
													if ($customButtons()[$.get(button)]) $$render(consequent_4); else $$render(alternate, false);
												},
												true
											);
										}

										$.append($$anchor, fragment_5);
									};

									$.if(
										node_4,
										($$render) => {
											if ($.get(button) == 'today') $$render(consequent_3); else $$render(alternate_1, false);
										},
										true
									);
								}

								$.append($$anchor, fragment_4);
							};

							$.if(
								node_3,
								($$render) => {
									if ($.get(button) == 'next') $$render(consequent_2); else $$render(alternate_2, false);
								},
								true
							);
						}

						$.append($$anchor, fragment_3);
					};

					$.if(
						node_2,
						($$render) => {
							if ($.get(button) == 'prev') $$render(consequent_1); else $$render(alternate_3, false);
						},
						true
					);
				}

				$.append($$anchor, fragment_2);
			};

			$.if(node_1, ($$render) => {
				if ($.get(button) == 'title') $$render(consequent); else $$render(alternate_4, false);
			});
		}

		$.append($$anchor, fragment_1);
	});

	$.append($$anchor, fragment);
	$.pop();
	$$cleanup();
}

var root_3 = $.template(`<div><!></div>`);
var root_1 = $.template(`<div></div>`);
var root$1 = $.template(`<nav></nav>`);

function Toolbar($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $headerToolbar = () => $.store_get(headerToolbar, '$headerToolbar', $$stores);
	const $theme = () => $.store_get(theme, '$theme', $$stores);
	let { headerToolbar, theme } = getContext('state');
	let sections = $.mutable_state({ start: [], center: [], end: [] });

	$.legacy_pre_effect(
		() => (
			$.get(sections),
			$headerToolbar()
		),
		() => {
			for (let key of keys($.get(sections))) {
				$.mutate(sections, $.get(sections)[key] = $headerToolbar()[key].split(' ').map((group) => group.split(',')));
			}
		}
	);

	$.legacy_pre_effect_reset();
	$.init();

	var nav = root$1();

	$.each(nav, 5, () => keys($.get(sections)), $.index, ($$anchor, key) => {
		var div = root_1();

		$.each(div, 5, () => $.get(sections)[$.get(key)], $.index, ($$anchor, buttons) => {
			var fragment = $.comment();
			var node = $.first_child(fragment);

			{
				var consequent = ($$anchor) => {
					var div_1 = root_3();
					var node_1 = $.child(div_1);

					Buttons(node_1, {
						get buttons() {
							return $.get(buttons);
						}
					});

					$.reset(div_1);
					$.template_effect(() => $.set_class(div_1, $theme().buttonGroup));
					$.append($$anchor, div_1);
				};

				var alternate = ($$anchor) => {
					Buttons($$anchor, {
						get buttons() {
							return $.get(buttons);
						}
					});
				};

				$.if(node, ($$render) => {
					if ($.get(buttons).length > 1) $$render(consequent); else $$render(alternate, false);
				});
			}

			$.append($$anchor, fragment);
		});

		$.reset(div);
		$.template_effect(() => $.set_class(div, `ec-${$.get(key) ?? ''}`));
		$.append($$anchor, div);
	});

	$.reset(nav);
	$.template_effect(() => $.set_class(nav, $theme().toolbar));
	$.append($$anchor, nav);
	$.pop();
	$$cleanup();
}

function Auxiliary($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $_activeRange = () => $.store_get(_activeRange, '$_activeRange', $$stores);
	const $datesSet = () => $.store_get(datesSet, '$datesSet', $$stores);
	const $_view = () => $.store_get(_view, '$_view', $$stores);
	const $_auxiliary = () => $.store_get(_auxiliary, '$_auxiliary', $$stores);

	let {
		datesSet,
		_auxiliary,
		_activeRange,
		_queue,
		_view
	} = getContext('state');

	let debounceHandle = {};

	function runDatesSet(_activeRange) {
		if (isFunction($datesSet())) {
			debounce(
				() => $datesSet()({
					start: toLocalDate(_activeRange.start),
					end: toLocalDate(_activeRange.end),
					startStr: toISOString(_activeRange.start),
					endStr: toISOString(_activeRange.end),
					view: toViewWithLocalDates($_view())
				}),
				debounceHandle,
				_queue
			);
		}
	}

	$.legacy_pre_effect(() => ($_activeRange()), () => {
		runDatesSet($_activeRange());
	});

	$.legacy_pre_effect_reset();
	$.init();

	var fragment = $.comment();
	var node = $.first_child(fragment);

	$.each(node, 1, $_auxiliary, $.index, ($$anchor, component) => {
		var fragment_1 = $.comment();
		var node_1 = $.first_child(fragment_1);

		$.component(node_1, () => $.get(component), ($$anchor, $$component) => {
			$$component($$anchor, {});
		});

		$.append($$anchor, fragment_1);
	});

	$.append($$anchor, fragment);
	$.pop();
	$$cleanup();
}

var root = $.template(`<div><!> <!></div> <!>`, 1);

function Calendar($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $_events = () => $.store_get(_events, '$_events', $$stores);
	const $_interaction = () => $.store_get(_interaction, '$_interaction', $$stores);
	const $date = () => $.store_get(date, '$date', $$stores);
	const $duration = () => $.store_get(duration, '$duration', $$stores);
	const $hiddenDays = () => $.store_get(hiddenDays, '$hiddenDays', $$stores);
	const $_queue = () => $.store_get(_queue, '$_queue', $$stores);
	const $_queue2 = () => $.store_get(_queue2, '$_queue2', $$stores);
	const $_bodyEl = () => $.store_get(_bodyEl, '$_bodyEl', $$stores);
	const $_scrollable = () => $.store_get(_scrollable, '$_scrollable', $$stores);
	const $theme = () => $.store_get(theme, '$theme', $$stores);
	const $_iClass = () => $.store_get(_iClass, '$_iClass', $$stores);
	const $height = () => $.store_get(height, '$height', $$stores);
	const $view = () => $.store_get(view, '$view', $$stores);
	const $_viewComponent = () => $.store_get(_viewComponent, '$_viewComponent', $$stores);
	let plugins = $.prop($$props, 'plugins', 24, () => []);
	let options = $.prop($$props, 'options', 24, () => ({}));
	let state = new State(plugins(), options());

	setContext('state', state);

	let {
		_viewComponent,
		_bodyEl,
		_interaction,
		_iClass,
		_events,
		_queue,
		_queue2,
		_tasks,
		_scrollable,
		date,
		duration,
		hiddenDays,
		height,
		theme,
		view
	} = state;

	// Reactively update options that did change
	let prevOptions = { ...options() };

	function setOption(name, value) {
		state._set(name, value);
		return this;
	}

	function getOption(name) {
		let value = state._get(name);

		return value instanceof Date ? toLocalDate(value) : value;
	}

	function refetchEvents() {
		state._fetchedRange.set({ start: undefined, end: undefined });
		return this;
	}

	function getEvents() {
		return $_events().map(toEventWithLocalDates);
	}

	function getEventById(id) {
		for (let event of $_events()) {
			if (event.id == id) {
				return toEventWithLocalDates(event);
			}
		}

		return null;
	}

	function addEvent(event) {
		event = createEvents([event])[0];
		$_events().push(event);
		$.store_set(_events, $_events());
		return toEventWithLocalDates(event);
	}

	function updateEvent(event) {
		for (let e of $_events()) {
			if (e.id == event.id) {
				event = createEvents([event])[0];
				assign(e, event);
				$.store_set(_events, $_events());
				return toEventWithLocalDates(event);
			}
		}

		return null;
	}

	function removeEventById(id) {
		let idx = $_events().findIndex((event) => event.id == id);

		if (idx >= 0) {
			$_events().splice(idx, 1);
			$.store_set(_events, $_events());
		}

		return this;
	}

	function getView() {
		return toViewWithLocalDates(get(state._view));
	}

	function unselect() {
		$_interaction().action?.unselect();
		return this;
	}

	function dateFromPoint(x, y) {
		let dayEl = getElementWithPayload(x, y);

		if (dayEl) {
			let info = getPayload(dayEl)(x, y);

			info.date = toLocalDate(info.date);
			return info;
		}

		return null;
	}

	function next() {
		$.store_set(date, nextDate($date(), $duration()));
		return this;
	}

	function prev() {
		$.store_set(date, prevDate($date(), $duration(), $hiddenDays()));
		return this;
	}

	beforeUpdate(() => {
		flushDebounce($_queue());
	});

	afterUpdate(() => {
		flushDebounce($_queue2());
		task(recheckScrollable, null, _tasks);
	});

	function recheckScrollable() {
		if ($_bodyEl()) {
			$.store_set(_scrollable, hasYScroll($_bodyEl()));
		}
	}

	$.legacy_pre_effect(() => ($.deep_read_state(options())), () => {
		for (let [name, value] of diff(options(), prevOptions)) {
			setOption(name, value);
		}
	});

	$.legacy_pre_effect_reset();
	$.init();

	var fragment = root();

	$.event('resize', $.window, recheckScrollable);

	var div = $.first_child(fragment);
	var node = $.child(div);

	Toolbar(node, {});

	var node_1 = $.sibling(node, 2);

	$.component(node_1, $_viewComponent, ($$anchor, $$component) => {
		$$component($$anchor, {});
	});

	$.reset(div);

	var node_2 = $.sibling(div, 2);

	Auxiliary(node_2, {});

	$.template_effect(
		($0) => {
			$.set_class(div, `${$theme().calendar ?? ''} ${$theme().view ?? ''}${($_scrollable() ? ' ' + $theme().withScroll : '') ?? ''}${($_iClass() ? ' ' + $theme()[$_iClass()] : '') ?? ''}`);
			$.set_attribute(div, 'role', $0);
			$.set_style(div, 'height', $height());
		},
		[
			() => listView($view()) ? 'list' : 'table'
		],
		$.derived_safe_equal
	);

	$.append($$anchor, fragment);
	$.bind_prop($$props, 'setOption', setOption);
	$.bind_prop($$props, 'getOption', getOption);
	$.bind_prop($$props, 'refetchEvents', refetchEvents);
	$.bind_prop($$props, 'getEvents', getEvents);
	$.bind_prop($$props, 'getEventById', getEventById);
	$.bind_prop($$props, 'addEvent', addEvent);
	$.bind_prop($$props, 'updateEvent', updateEvent);
	$.bind_prop($$props, 'removeEventById', removeEventById);
	$.bind_prop($$props, 'getView', getView);
	$.bind_prop($$props, 'unselect', unselect);
	$.bind_prop($$props, 'dateFromPoint', dateFromPoint);
	$.bind_prop($$props, 'next', next);
	$.bind_prop($$props, 'prev', prev);

	var $$pop = $.pop({
		setOption,
		getOption,
		refetchEvents,
		getEvents,
		getEventById,
		addEvent,
		updateEvent,
		removeEventById,
		getView,
		unselect,
		dateFromPoint,
		next,
		prev
	});

	$$cleanup();
	return $$pop;
}

class index extends Calendar {
    destroy() {
        this.$destroy();
    }

    get view() {
        return this.getView();
    }
}

export { DAY_IN_SECONDS, addDay, addDuration, ancestor, assign, bgEvent, btnTextDay, btnTextMonth, btnTextWeek, btnTextYear, ceil, cloneDate, cloneEvent, copyTime, createDate, createDateRange, createDuration, createElement, createEventChunk, createEventClasses, createEventContent, createEventSources, createEvents, createResource, createResources, createSlotTimeLimits, createTimes, createView, datesEqual, debounce, index as default, eventIntersects, floor, flushDebounce, getElementWithPayload, getPayload, getWeekNumber, ghostEvent, hasPayload, hasYScroll, height, helperEvent, identity, intl, intlRange, isArray, isFunction, keyEnter, keys, limitToRange, listView, listen, max, min, nextClosestDay, nextDate, noTimePart, noop, outsideEvent, outsideRange, pointerEvent, prepareEventChunks, prevClosestDay, prevDate, previewEvent, rect, repositionEvent, resourceBackgroundColor, resourceTextColor, run, runAll, runReposition, setContent, setMidnight, setPayload, sortEventChunks, subtractDay, subtractDuration, symbol, task, themeView, timelineView, toEventWithLocalDates, toISOString, toLocalDate, toSeconds, toViewWithLocalDates, viewResources };
